import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useMemo } from 'react';
import { 
  calculateMonthlySummary, 
  calculateFocoScore,
  MonthlySummary,
} from '@/lib/foco-calculations';
import { useGoals } from './useGoals';

export interface YearlySummary {
  total_income: number;
  total_fixed_expenses: number;
  total_variable_expenses: number;
  total_savings: number;
  total_investments: number;
  total_debt_payments: number;
  real_cash_flow: number;
  savings_rate: number;
  debt_ratio: number;
  fixed_expense_ratio: number;
  variable_expense_ratio: number;
  foco_score: number;
  monthly_breakdown: MonthlyBreakdown[];
}

export interface MonthlyBreakdown {
  month: number;
  monthName: string;
  income: number;
  fixedExpenses: number;
  variableExpenses: number;
  savings: number;
  investments: number;
  debtPayments: number;
  cashFlow: number;
  focoScore: number;
}

const monthNames = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

export function useYearlySummary(year: number) {
  const { user } = useAuth();
  const { goals } = useGoals();

  // Fetch all transactions for the year
  const { data: transactions, isLoading } = useQuery({
    queryKey: ['yearly-transactions', user?.id, year],
    queryFn: async () => {
      if (!user) return [];
      
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  const yearlySummary = useMemo(() => {
    if (!transactions?.length) return null;

    const activeGoals = goals
      .filter(g => g.status === 'active')
      .map(g => ({
        id: g.id,
        name: g.name,
        target_amount: Number(g.target_amount),
        current_amount: Number(g.current_amount),
        status: g.status as 'active' | 'completed' | 'paused',
      }));

    // Group transactions by month
    const monthlyData: Record<number, {
      income: number;
      fixedExpenses: number;
      variableExpenses: number;
      savings: number;
      investments: number;
      debtPayments: number;
    }> = {};

    // Initialize all months
    for (let m = 1; m <= 12; m++) {
      monthlyData[m] = {
        income: 0,
        fixedExpenses: 0,
        variableExpenses: 0,
        savings: 0,
        investments: 0,
        debtPayments: 0,
      };
    }

    // Aggregate transactions by month
    transactions.forEach(t => {
      const month = new Date(t.date).getMonth() + 1;
      const amount = Number(t.amount);
      
      switch (t.type) {
        case 'income':
          monthlyData[month].income += amount;
          break;
        case 'fixed_expense':
          monthlyData[month].fixedExpenses += amount;
          break;
        case 'variable_expense':
          monthlyData[month].variableExpenses += amount;
          break;
        case 'saving':
          monthlyData[month].savings += amount;
          break;
        case 'investment':
          monthlyData[month].investments += amount;
          break;
        case 'debt_payment':
          monthlyData[month].debtPayments += amount;
          break;
      }
    });

    // Calculate yearly totals
    let totalIncome = 0;
    let totalFixedExpenses = 0;
    let totalVariableExpenses = 0;
    let totalSavings = 0;
    let totalInvestments = 0;
    let totalDebtPayments = 0;

    const monthlyBreakdown: MonthlyBreakdown[] = [];

    for (let m = 1; m <= 12; m++) {
      const data = monthlyData[m];
      totalIncome += data.income;
      totalFixedExpenses += data.fixedExpenses;
      totalVariableExpenses += data.variableExpenses;
      totalSavings += data.savings;
      totalInvestments += data.investments;
      totalDebtPayments += data.debtPayments;

      // Calculate monthly summary for FOCO score
      const monthlySummary = calculateMonthlySummary(
        data.income,
        data.fixedExpenses,
        data.variableExpenses,
        data.savings,
        data.investments,
        data.debtPayments
      );

      const monthFocoScore = data.income > 0 
        ? calculateFocoScore(monthlySummary, activeGoals) 
        : 0;

      monthlyBreakdown.push({
        month: m,
        monthName: monthNames[m - 1],
        income: data.income,
        fixedExpenses: data.fixedExpenses,
        variableExpenses: data.variableExpenses,
        savings: data.savings,
        investments: data.investments,
        debtPayments: data.debtPayments,
        cashFlow: data.income - data.fixedExpenses - data.variableExpenses - data.savings - data.investments - data.debtPayments,
        focoScore: monthFocoScore,
      });
    }

    // Calculate yearly ratios
    const savingsRate = totalIncome > 0 
      ? (totalSavings + totalInvestments) / totalIncome 
      : 0;
    const debtRatio = totalIncome > 0 
      ? totalDebtPayments / totalIncome 
      : 0;
    const fixedExpenseRatio = totalIncome > 0 
      ? totalFixedExpenses / totalIncome 
      : 0;
    const variableExpenseRatio = totalIncome > 0 
      ? totalVariableExpenses / totalIncome 
      : 0;
    const realCashFlow = totalIncome - totalFixedExpenses - totalVariableExpenses - totalSavings - totalInvestments - totalDebtPayments;

    // Calculate yearly FOCO score based on yearly totals
    const yearlySummaryCalc = calculateMonthlySummary(
      totalIncome,
      totalFixedExpenses,
      totalVariableExpenses,
      totalSavings,
      totalInvestments,
      totalDebtPayments
    );
    const yearlyFocoScore = totalIncome > 0 
      ? calculateFocoScore(yearlySummaryCalc, activeGoals) 
      : 0;

    return {
      total_income: totalIncome,
      total_fixed_expenses: totalFixedExpenses,
      total_variable_expenses: totalVariableExpenses,
      total_savings: totalSavings,
      total_investments: totalInvestments,
      total_debt_payments: totalDebtPayments,
      real_cash_flow: realCashFlow,
      savings_rate: savingsRate,
      debt_ratio: debtRatio,
      fixed_expense_ratio: fixedExpenseRatio,
      variable_expense_ratio: variableExpenseRatio,
      foco_score: yearlyFocoScore,
      monthly_breakdown: monthlyBreakdown,
    } as YearlySummary;
  }, [transactions, goals]);

  return {
    summary: yearlySummary,
    isLoading,
    hasActiveGoals: goals.filter(g => g.status === 'active').length > 0,
  };
}
